/** O que a area do trader le da API (spec 004 e 007). So forma de dado: regra fica no backend. */
import type { EstadoNaCorretora, Notificacao, ResumoDoSaldo, Tom } from "@mtc/ui";

export type Etapa = "TESTE" | "SIMULADOR" | "MESA_REAL";
export type StatusMatricula = "AGUARDANDO_INICIO" | "ATIVA" | "SUSPENSA" | "ENCERRADA";
export type StatusSolicitacao = "PENDENTE" | "AGUARDANDO_PAGAMENTO" | "APROVADA" | "RECUSADA" | "CANCELADA" | "REVOGADA";

export type ModuloEtapas = {
  familia: "EXAME" | "FAST" | "BLACK";
  familia_rotulo: string;
  etapa: Etapa;
  etapa_rotulo: string;
  /** As etapas da familia, na ordem. Vem do backend (a regra mora la); a tela so desenha. */
  caminho: { etapa: Etapa; rotulo: string }[];
  proxima_etapa: Etapa | null;
  inicio: string;
  teste_ate: string | null;
  teste_sem_prazo: boolean;
  prazo_motivo: string | null;
  dias_restantes_do_teste: number | null;
  teste_vencido: boolean;
  teste_aprovado_em: string | null;
  /** O inicio de cada etapa ja percorrida, na ordem do caminho (a etapa 1 sem registro e o inicio do plano). */
  inicios?: { etapa: Etapa; rotulo: string; numero: number; dia: string | null }[];
  /** O proximo avanco como a regra da familia diz (prazo prometido da liberacao, se pede plataforma). */
  proximo_avanco?: { para: Etapa; para_rotulo: string; portao: string; pede_plataforma: boolean; pede_subconta_nova: boolean; prazo: string } | null;
  /**
   * Avanco aprovado (e pago, quando tem portao) esperando a equipe criar ou mover a conta (spec 038, 1.5). Preenchido,
   * a tela mostra a promessa com o prazo e nao oferece "Pedir analise": o backend recusa o pedido de novo com 409.
   */
  avanco_aguardando_liberacao?: { para: Etapa | null; para_rotulo: string | null; aprovado_em: string | null; pago_em: string | null; prazo: string | null; vence_em: string | null } | null;
};
export type ModuloCorretora = { plataforma: "PROFIT_ONE" | "PROFIT_PRO" | null; plataforma_rotulo: string | null; subconta: string | null; mesa: string | null; mesa_nome: string | null; vinculada_em: string | null };
/**
 * A conta da Global na corretora (modulo `nelogica-v2`, spec 043): o numero e o estado lido da API. `estado` nulo: plano
 * encerrado (a conta pode estar com outro plano agora, entao nao se diz nada dela).
 */
export type ModuloContaNaCorretora = { conta: string; vinculada_em: string | null; estado: EstadoNaCorretora | null; /** Spec 044: a etapa (Challenge ou Funded) e desde quando. */ etapa?: "CHALLENGE" | "FUNDED" | null; etapa_rotulo?: string | null; etapa_desde?: string | null; bloqueada_desde?: string | null; lida_em?: string | null; confirmada_em?: string | null };
/** O saldo da conta (modulo `saldo`, spec 043): o resumo vem pronto do backend; o tipo mora no design system. */
export type ModuloSaldo = ResumoDoSaldo;
export type ModuloAgendamento = { inicio: string; inicio_formatado: string; escolhida_em: string; trocada_em: string };
/** `SUSPENSA`: a taxa de manutencao da Funded venceu e suspendeu o plano (spec 064, modulo `assinatura`). */
export type SituacaoMensalidade = "EM_DIA" | "VENCE_EM_BREVE" | "VENCE_HOJE" | "VENCIDA" | "VALOR_A_DEFINIR" | "SEM_COBRANCA" | "SUSPENSA";
/** Uma mensalidade (spec 007, secao 12). A situacao e o texto vem do backend (a regra mora la). */
export type Mensalidade = {
  id: string;
  numero: number;
  vencimento: string;
  vencimento_formatado: string;
  valor_centavos: number | null;
  status: "A_PAGAR" | "PAGA" | "CANCELADA";
  situacao: SituacaoMensalidade | "PAGA" | "CANCELADA";
  situacao_rotulo: string;
  dias_de_atraso: number;
  como_pagou: "PLATAFORMA" | "POR_FORA" | null;
  meio: string | null;
  paga_em: string | null;
  do_avanco: boolean;
  pode_pagar: boolean;
  /** Link do WhatsApp da marca com a mensagem pronta, pra quem pagou por fora (M8). */
  whatsapp?: string | null;
  /** Lancada a mao pela equipe (spec 041, 3.3): numeracao propria ("Avulsa 1"). */
  avulsa?: boolean;
  /**
   * Taxa de manutencao da Funded (spec 064): o titulo pronto ("Taxa de manutenção 2"), o valor em dolar, a cotacao que
   * virou o real ("5.1527", nula ate chegar), e, na parcela que suspendeu o plano, o ultimo dia pra pagar.
   */
  titulo?: string;
  valor_usd_centavos?: number | null;
  cotacao?: string | null;
  cotacao_publicada_em?: string | null;
  suspendeu_no_dia?: string | null;
  suspensa_ate?: string | null;
  prazo_encerrado?: boolean;
};
/** Uma parcela do calendario previsto (spec 041, 3.7): so leitura, nao se paga nem bloqueia. */
export type MensalidadePrevista = { numero: number; vencimento: string; vencimento_formatado: string; valor_centavos: number | null; valor_usd_centavos?: number | null; prevista: true };
/** "Mensalidade 3" ou "Avulsa 1": a avulsa tem numeracao propria. O titulo que veio pronto da API vale primeiro. */
export const tituloDaParcela = (p: { numero: number; avulsa?: boolean; titulo?: string }) => p.titulo ?? `${p.avulsa ? "Avulsa" : "Mensalidade"} ${p.numero}`;
export type ModuloMensalidade = {
  paga_mensalidade: boolean;
  sem_cobranca_motivo: string | null;
  /** A regra do plano com os valores desta compra (vem do checkout), pronta pra mostrar. */
  texto: string | null;
  valor_centavos: number | null;
  situacao: SituacaoMensalidade;
  situacao_rotulo: string;
  proxima: Mensalidade | null;
  vencidas: number;
  ultima_paga: { numero: number; vencimento: string; paga_em: string; como_pagou: string } | null;
  /** O nome da cobranca quando nao e "Mensalidade" (spec 064: "Taxa de manutenção"). */
  rotulo?: string;
  /** Spec 065: o tamanho do ciclo (o que o ciclo aberto guardou, ou o de agora). So na taxa de manutencao. */
  ciclo_dias?: number;
  /** Cobranca em dolar (spec 064): o valor de cada parcela em US$; o real sai pela cotacao do dia em que ela nasce. */
  valor_usd_centavos?: number | null;
  /** Suspenso pela taxa: o ultimo dia (AAAA-MM-DD) pra pagar e o plano voltar (regulamento 5.3). */
  suspensa_ate?: string | null;
};
export type MensalidadesDoPlano = { matricula: { id: string; plano: { slug: string; nome: string }; status: StatusMatricula }; rotulo?: string; resumo: ModuloMensalidade | null; mensalidades: Mensalidade[]; previstas?: MensalidadePrevista[] };
/** `GET /v1/me/mensalidades`. A Prop nao manda `rotulo` (a tela diz "Mensalidades"); a Global manda "Taxa de manutenção". */
export type ListaDeMensalidades = { rotulo?: string; itens: MensalidadesDoPlano[] };

/**
 * O pedido de aprovacao do Challenge (spec 064, modulo `nelogica-v2`): se pode pedir agora (ou a frase de por que nao,
 * pronta pra mostrar), o prazo da analise e o ultimo pedido do plano. A regra mora no backend; a tela so mostra.
 */
export type ModuloAprovacao = {
  pode_pedir: boolean;
  motivo: string | null;
  prazo_da_analise: string;
  pedido: {
    id: string;
    status: "PENDENTE" | "AGUARDANDO_PAGAMENTO" | "APROVADA" | "RECUSADA" | "CANCELADA";
    criado_em: string;
    /** A taxa de ativacao cobrada (nula: o plano nao cobra). */
    valor_centavos: number | null;
    pago: boolean;
    aguardando_liberacao: boolean;
    prazo: string | null;
    /** O motivo da recusa. */
    observacao: string | null;
    liberada_no_dia: string | null;
  } | null;
};
/** O que um modulo bloqueia (ex.: mensalidade vencida): o motivo, a mensagem e o que fazer. */
export type Bloqueio = { motivo: string; mensagem: string; acao: { rotulo: string; link: string } | null };

export type ProximoPasso = { tipo: "AGENDAR_INICIO" | "AGUARDANDO_LIBERACAO" | "REAGENDAR" | (string & {}); titulo: string; texto: string };

export type Matricula = {
  id: string;
  plano: { slug: string; nome: string; nome_curto: string | null; contratos: number | null };
  status: StatusMatricula;
  tem_acesso: boolean;
  origem: string;
  comprada_em: string | null;
  ativada_em: string | null;
  suspensa: { em: string; motivo: string | null } | null;
  encerrada: { em: string; motivo: string | null } | null;
  proximo_passo: ProximoPasso | null;
  /** `corretora` tem a forma do modulo ligado: `ModuloCorretora` na Prop (nelogica-v1), `ModuloContaNaCorretora` na Global (nelogica-v2). */
  modulos: { etapas?: ModuloEtapas; corretora?: ModuloCorretora; agendamento?: ModuloAgendamento; mensalidade?: ModuloMensalidade; beneficios?: ModuloBeneficios; saldo?: ModuloSaldo; aprovacao?: ModuloAprovacao };
  bloqueio: Bloqueio | null;
};

export type Solicitacao = {
  id: string;
  tipo: string;
  titulo: string;
  resumo: string;
  status: StatusSolicitacao;
  mensagem: string | null;
  observacao: string | null;
  valor_centavos: number | null;
  /** Pago pela plataforma ou confirmado por fora pela equipe (spec 007, M7 e M8). */
  pagamento: { pago_em: string; como: "PLATAFORMA" | "POR_FORA"; meio: string | null; observacao?: string | null } | null;
  pode_pagar: boolean;
  matricula: { id: string; plano: { slug: string; nome: string } };
  criada_em: string;
  decidida_em: string | null;
  /** Aprovado, esperando a equipe criar ou mover a conta na extranet (spec 015, decisao 4). */
  aguardando_liberacao?: boolean;
  /** Liberado: quando a conta nova ficou pronta (spec 038). */
  liberada_em?: string | null;
  liberada_no_dia?: string | null;
  prazo?: string | null;
};

export type ListaDeAvisos = { itens: (Notificacao & { tipo?: string; lida_em?: string | null })[]; nao_lidas: number };

export const STATUS_DA_MATRICULA: Record<StatusMatricula, { rotulo: string; tom: Tom }> = {
  AGUARDANDO_INICIO: { rotulo: "Aguardando início", tom: "info" },
  ATIVA: { rotulo: "Ativa", tom: "sucesso" },
  SUSPENSA: { rotulo: "Suspensa", tom: "alerta" },
  ENCERRADA: { rotulo: "Encerrada", tom: "neutro" },
};

export const STATUS_DA_SOLICITACAO: Record<StatusSolicitacao, { rotulo: string; tom: Tom }> = {
  PENDENTE: { rotulo: "Em análise", tom: "info" },
  AGUARDANDO_PAGAMENTO: { rotulo: "Aguardando pagamento", tom: "alerta" },
  APROVADA: { rotulo: "Aprovada", tom: "sucesso" },
  RECUSADA: { rotulo: "Recusada", tom: "erro" },
  CANCELADA: { rotulo: "Cancelada", tom: "neutro" },
  REVOGADA: { rotulo: "Revertida", tom: "erro" },
};

export const ABERTAS: StatusSolicitacao[] = ["PENDENTE", "AGUARDANDO_PAGAMENTO"];

/**
 * Os pedidos que mudam a etapa do plano e podem pedir um pagamento antes da liberacao: o avanco da Prop (a mensalidade
 * antecipada, spec 007, M7) e a aprovacao do Challenge da Global (a taxa de ativacao, spec 064). O tipo vem da API; aqui
 * so mora o nome do que se paga e o que ele libera, pra frase da tela.
 */
export type TextosDoPedido = {
  /** O que se paga, quando o valor nao veio ("a mensalidade antecipada"). */
  oQueSePaga: string;
  /** O fim da frase "Pague R$ X ...". */
  pague: string;
  /** Depois do pagamento, quem faz o que. */
  recebido: string;
  /** O titulo do aprovado esperando a conta. */
  aprovado: string;
  daConta: string;
  verPlano: string;
  /** O lembrete enquanto o pedido esta em analise (a Global pede pausar as operacoes, regulamento 4.5). */
  emAnalise?: string;
};
export const PEDIDOS_COM_PAGAMENTO: Record<string, TextosDoPedido> = {
  AVANCO_ETAPA: { oQueSePaga: "a mensalidade antecipada", pague: "pra liberar a nova etapa", recebido: "Um administrador está concluindo o seu avanço", aprovado: "Avanço aprovado", daConta: "da nova conta", verPlano: "Ver o plano na nova etapa" },
  APROVACAO_DO_CHALLENGE: {
    oQueSePaga: "a taxa de ativação",
    pague: "de taxa de ativação pra liberar a conta Funded",
    recebido: "Um administrador está liberando a sua conta Funded",
    aprovado: "Challenge aprovado",
    daConta: "da conta Funded",
    verPlano: "Ver o plano na Funded",
    emAnalise: "Mantenha as operações pausadas até a análise terminar: se operar e atingir o drawdown, a conta é cancelada.",
  },
};
export const ehPedidoDeEtapa = (s: { tipo: string }) => s.tipo in PEDIDOS_COM_PAGAMENTO;

/** Nomes curtos das etapas, pra barra de progresso. O nome completo vem da API (`etapa_rotulo`). */
export const ETAPA_CURTA: Record<Etapa, string> = { TESTE: "Teste", SIMULADOR: "Simulador", MESA_REAL: "Mesa Real" };
export const ETAPA_LONGA: Record<Etapa, string> = { TESTE: "Período de teste", SIMULADOR: "Simulador Remunerado", MESA_REAL: "Mesa Real" };

/** Beneficios prometidos na venda (spec 013, item 2) e, no plano bonus, de qual venda ele veio. */
export type ModuloBeneficios = {
  /** `duracao_dias`: temporario (nulo, permanente); `vale_ate`: o ultimo dia, quando o plano ja foi liberado. */
  itens: { id: string; nome: string; efeito: "ENTREGA" | "CONDICAO" | "PLANO_EM_DOBRO"; situacao: "A_HONRAR" | "HONRADO" | "CANCELADO" | "EM_VIGOR"; matricula_bonus_id: string | null; duracao_dias: number | null; vale_ate: string | null }[];
  bonus_de: { matricula_id: string; beneficio: string } | null;
};

/**
 * Pendencias da casca (spec 038, secao 4): o que conta como pendencia e regra, entao vem pronto do backend. A casca so
 * desenha o ponto no destino; `tom` nulo e sem ponto.
 */
export type Resumo = {
  pendencias: {
    meu_plano: { tom: "alerta" | null; motivo: string | null };
    financeiro: { tom: "alerta" | "erro" | null; motivo: string | null };
    academy: { respostas_novas: number };
  };
};
